import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  AdressevaelgerAdresseResponse,
  AdressevaelgerHusnummerResponse,
  AdressevaelgerResponse,
  DavaService,
} from '../../services/dava/dava.service';
import {
  AddressData,
  AddressSuggestion,
  ManualAddress,
  ManualAddressField,
} from '../../models/dava.model';
import { map, Subscription } from 'rxjs';

@Component({
  selector: 'lib-address-input',
  templateUrl: './address-input.component.html',
  styleUrls: ['./address-input.component.css'],
  standalone: false,
})
export class AddressInputComponent {
  @Output() addressSubmitted = new EventEmitter<AddressData>();
  @Output() manualAddressChange = new EventEmitter<ManualAddress>();
  @Output() manualChangeEvent = new EventEmitter<boolean>();
  @Output() resetErrorEvent = new EventEmitter<boolean>();

  @Input() manual = false;
  @Input() addressLabel = 'Adresse';
  @Input() showRequiredIndicator = true;
  @Input() errorInAddress = false;

  @Input() addressData!: AddressData | undefined;
  @Input() searchText: string = '';

  @Input() errorInCustomerStreet = false;
  @Input() errorInCustomerBuildingNumber = false;
  @Input() errorInCustomerFloor = false;
  @Input() errorInCustomerDoor = false;
  @Input() errorInCustomerAdditionalCity = false;
  @Input() errorInCustomerCity = false;
  @Input() errorInCustomerPostCode = false;

  manualAddress: ManualAddress = {
    customerStreet: '',
    customerBuildingNumber: '',
    customerFloor: null,
    customerDoor: null,
    customerAdditionalCity: null,
    customerCity: '',
    customerPostCode: '',
  };

  //this is the current request - this is so we can cancels the previous request when a new char is typed
  activeSearch: Subscription | undefined;
  searchDebounceTimer?: number;

  suggestions: AddressSuggestion[] = [];
  activeSuggestionIndex = -1;
  isOpen = false;
  isLoading = false;
  hasSearched = false;
  apiError = false;

  constructor(private davaService: DavaService) {}

  onSearchChange(value: string): void {
    this.searchText = value.trim();
    this.addressSubmitted.emit(undefined);
    this.resetErrorEvent.emit(false);
    this.errorInAddress = false;
    // ##### clear old search #####
    clearTimeout(this.searchDebounceTimer);
    this.activeSearch?.unsubscribe();
    // ############################

    this.resetResults();
    if (!this.searchText) {
      return;
    }

    this.searchDebounceTimer = window.setTimeout(() => {
      this.isLoading = true;
      this.hasSearched = true;
      this.isOpen = true;
      // calls the adressevaelgerAutoComplete on input change
      this.activeSearch = this.adressevaelgerAutoComplete(
        this.searchText,
      ).subscribe({
        next: (suggestions: any) => {
          this.suggestions = suggestions;
          this.activeSuggestionIndex = -1;
          this.isLoading = false;
        },
        error: () => {
          this.apiError = true;
          this.isLoading = false;
        },
      });
    }, 275);
  }

  /**
   * chooseSuggestion decides what to do based on how specific the selected result is
   * if (suggestion.type === 'husnummer')
   * - This is a complete data choice. It calls selectHusnummer(), which get the address details using its ID
   *
   * if (suggestion.type === 'navngivenvejpostnummer')
   * - This means the user chose a road and postcode / city, but not a house number.
   * - this trigger a call to showHouseNumberSuggestions(), this calls the API using the road name and postcode to get new suggestions
   *
   * if (suggestion.type === 'vejnavn')
   * - This is only a road name.......
   * - alls the API with the road name. The next dropdown should contain road-and-postcode choice
   *
   * if (suggestion.type === 'adresse')
   * - This is a complete address choice.
   * - It calls selectAdresse(), which fetches the address details using its ID.
   * @param suggestion
   * @returns void
   */
  chooseSuggestion(suggestion: AddressSuggestion): void {
    console.log(suggestion);

    if (suggestion.type === 'husnummer') {
      this.selectHusnummer(suggestion);
      return;
    }

    if (suggestion.type === 'adresse') {
      this.selectAdresse(suggestion);
      return;
    }

    if (
      suggestion.type === 'navngivenvejpostnummer' &&
      suggestion.vejnavn &&
      suggestion.postnr
    ) {
      this.adressevaelgerHusnumreForVej(suggestion);
      return;
    }

    if (suggestion.type === 'vejnavn' && suggestion.vejnavn) {
      this.adressevaelgerHusnumreForVej(suggestion);
      return;
    }

    this.apiError = true;
    this.closeDropdown();
  }

  adressevaelgerAutoComplete(query: string) {
    const autocompleteResponse = this.davaService
      .adressevaelgerAutoComplete(query)
      .pipe(map((response) => this.mapSuggestions(response)));

    return autocompleteResponse;
  }
  adressevaelgerHusnumreForVej(suggestion: AddressSuggestion): void {
    this.searchText = suggestion.tekst;
    this.isLoading = true;
    this.apiError = false;

    this.davaService
      .adressevaelgerHusnumreForVej(suggestion.vejnavn!, suggestion.postnr!)
      .pipe(map((response) => this.mapSuggestions(response)))
      .subscribe({
        next: (suggestions) => {
          this.suggestions = suggestions;
          this.activeSuggestionIndex = -1;
          this.isLoading = false;
          this.isOpen = true;
        },
        error: () => {
          this.suggestions = [];
          this.apiError = true;
          this.isLoading = false;
          this.isOpen = true;
        },
      });
  }

  selectHusnummer(suggestion: AddressSuggestion): void {
    const id = suggestion.id;
    if (!id) {
      this.apiError = true;
      this.isOpen = true;
      return;
    }

    this.isLoading = true;
    this.apiError = false;
    this.davaService.adressevaelgerHusnummerById(id).subscribe({
      next: (response: AdressevaelgerHusnummerResponse) => {
        const address = response.husnummer;
        this.searchText = suggestion.tekst;
        this.addressSubmitted.emit({
          id,
          vejnavn: address.vejnavn,
          husnr: address.husnummertekst,
          postnr: address.postnummer.postnr,
          postnrnavn: address.postnummer.navn,
        });
        this.isLoading = false;
        this.closeDropdown();
      },
      error: () => {
        this.apiError = true;
        this.isLoading = false;
        this.isOpen = true;
      },
    });
  }

  selectAdresse(suggestion: AddressSuggestion): void {
    const id = suggestion?.id;
    if (!id) {
      this.apiError = true;
      this.isOpen = true;
      return;
    }

    this.isLoading = true;
    this.apiError = false;
    this.davaService.adressevaelgerAdresseById(id).subscribe({
      next: (response: AdressevaelgerAdresseResponse) => {
        const address = response.adresse.husnummer;
        this.searchText = suggestion.tekst;
        this.addressSubmitted.emit({
          id,
          vejnavn: address.vejnavn,
          husnr: address.husnummertekst,
          postnr: address.postnummer.postnr,
          postnrnavn: address.postnummer.navn,
        });
        this.isLoading = false;
        this.closeDropdown();
      },
      error: () => {
        this.apiError = true;
        this.isLoading = false;
        this.isOpen = true;
      },
    });
  }

  // ###########################################
  // ################# HELPERS #################
  // ###########################################

  mapSuggestions(response: AdressevaelgerResponse): AddressSuggestion[] {
    const suggestions = response.fund ?? [];

    const mapedSuggestions = suggestions.map((result) => ({
      tekst: result.titel,
      type: result.type,
      id: result.id ?? undefined,
      vejnavn: result.vejnavn ?? result.vejNavn,
      postnr: result.postnr,
    }));

    return mapedSuggestions;
  }

  resetResults(): void {
    // this.suggestions = [];
    this.activeSuggestionIndex = -1;
    this.apiError = false;
    this.hasSearched = false;
    this.isLoading = false;
  }

  clearAddressInput() {
    this.addressSubmitted.emit(undefined);
    this.searchText = '';
    this.resetResults;
  }

  toggleManual(): void {
    this.manual = !this.manual;
    this.manualChangeEvent.emit(this.manual);
    this.closeDropdown();
  }

  closeDropdown(): void {
    this.isOpen = false;
    this.activeSuggestionIndex = -1;
  }

  onSearchFocus(): void {
    if (this.suggestions.length || this.isLoading || this.apiError) {
      this.isOpen = true;
    }
  }

  manualFieldChange(field: ManualAddressField): void {
    this.clearManualFieldError(field);
    this.manualAddressChange.emit(this.manualAddress);
  }

  clearManualFieldError(field: ManualAddressField): void {
    switch (field) {
      case 'customerStreet':
        this.errorInCustomerStreet = false;
        break;
      case 'customerBuildingNumber':
        this.errorInCustomerBuildingNumber = false;
        break;
      case 'customerFloor':
        this.errorInCustomerFloor = false;
        break;
      case 'customerDoor':
        this.errorInCustomerDoor = false;
        break;
      case 'customerAdditionalCity':
        this.errorInCustomerAdditionalCity = false;
        break;
      case 'customerCity':
        this.errorInCustomerCity = false;
        break;
      case 'customerPostCode':
        this.errorInCustomerPostCode = false;
        break;
    }
  }
}
